/**
 * Teacher Juno's reaction bubble (WOW · feel): Juno pops up from the bottom-left corner with an
 * expressive pose and a short line (spoken when nobody else is talking); for big moments Scribble
 * hops in on the right and cheers. Tap anywhere on it to dismiss. Storybook style, world accents.
 */
import { useEffect, useMemo } from 'react';
import { WORLD_ART } from '../../art/worlds';
import { useT, type TKey } from '../../lib/i18n';
import { useJunoBus, type JunoReaction } from '../../lib/juno';
import { play } from '../../lib/sfx';
import { buzz } from '../../lib/haptics';
import { isSpeaking, speak } from '../../lib/tts';
import { useApp } from '../../state/store';
import { Scribble, TeacherJuno } from '../characters';
import { useWorldNow } from './worldNow';
import './feel.css';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Speak only when the lesson voice-over (or anything else) is quiet. */
async function speakWhenIdle(text: string) {
  for (let i = 0; i < 8 && isSpeaking(); i++) await sleep(250);
  if (isSpeaking()) return;
  void speak(text);
}

export function JunoReactions() {
  const cur = useJunoBus((s) => s.current);
  if (!cur) return null;
  return <Bubble key={cur.id} r={cur} />;
}

function Bubble({ r }: { r: JunoReaction }) {
  const t = useT();
  const clear = useJunoBus((s) => s.clear);
  const worldNow = useWorldNow((s) => s.world);
  const reduced = useApp((s) => s.settings.reducedMotion);
  const world = r.world ?? worldNow;
  const accents = WORLD_ART[world]?.theme.accents ?? WORLD_ART.intro.theme.accents;
  const line = useMemo(() => t(`fx.j.${r.kind}.${r.variant}` as TKey, r.vars), [t, r]);
  const big = !!r.scribble;

  useEffect(() => {
    play(r.kind === 'comeback' || r.kind === 'hello' ? 'junoHello' : r.kind === 'stepDone' ? 'stepDone' : 'pop');
    if (big && r.kind !== 'retry') setTimeout(() => play('cheer'), 380);
    buzz(r.kind === 'stepDone' ? 'step' : r.kind === 'retry' ? 'select' : 'success');
    if (r.voice) void speakWhenIdle(line);
    const id = setTimeout(() => clear(r.id), r.ms);
    return () => clearTimeout(id);
  }, [r, line, big, clear]);

  const vars = { ['--jr-ms' as string]: `${Math.max(600, r.ms - 400)}ms`, ['--jr-a' as string]: accents[0], ['--jr-b' as string]: accents[1], ['--jr-c' as string]: accents[2] };
  return (
    <>
      <div className={`juno-react jr-${r.kind}${reduced ? ' reduced' : ''}`} role="status" aria-live="polite" data-testid="juno-reaction" data-kind={r.kind} style={vars} onClick={() => clear(r.id)}>
        <div className="jr-juno">
          <TeacherJuno face={r.face} size={r.kind === 'stepDone' ? 92 : 118} />
        </div>
        <div className="jr-bubble">
          <span className="jr-dots" aria-hidden>
            <i />
            <i />
            <i />
          </span>
          <p>{line}</p>
        </div>
      </div>
      {big && (
        <div className={`juno-react-side jr-scribble mood-${r.scribble}${reduced ? ' reduced' : ''}`} style={vars} aria-hidden onClick={() => clear(r.id)}>
          <Scribble mood={r.scribble} size={96} />
          {r.kind !== 'retry' && Array.from({ length: 6 }, (_, i) => <span key={i} className="jr-spark" style={{ ['--i' as string]: i, background: accents[i % 3] }} />)}
        </div>
      )}
    </>
  );
}
